import axios from 'axios';
import { httpApi } from './http';
import type { 
  Category, 
  CategoryApiResponse, 
  CategoryPaginationParams, 
  CreateCategoryRequest, 
  UpdateCategoryRequest,
  CreateSecondaryCategoryRequest,
  UpdateSecondaryCategoryRequest
} from '../types/category';
const api = httpApi;

export class CategoryService {
  // Primary Categories APIs
  
  static async getCategories(params: CategoryPaginationParams = {}): Promise<CategoryApiResponse> {
    try {
      const requestBody = {
        page: params.page || 1,
        limit: params.limit || 10,
        isActive: params.isActive,
        search: params.search,
      };
      
      const response = await api.post<CategoryApiResponse>('/categories', requestBody);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error fetching categories:', error);
      throw new Error('Failed to fetch categories');
    }
  }

  static async getPrimaryCategories(isActive?: boolean): Promise<CategoryApiResponse> {
    try {
      const requestBody = isActive !== undefined ? { isActive } : {};
      
      const response = await api.post<CategoryApiResponse>('/categories/primary', requestBody);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error fetching primary categories:', error);
      throw new Error('Failed to fetch primary categories');
    }
  }

  static async getCategoryById(id: string): Promise<CategoryApiResponse> {
    try {
      const response = await api.get<CategoryApiResponse>(`/categories/${id}`);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error fetching category:', error);
      throw new Error('Failed to fetch category details');
    }
  }

  static async getCategoryBySlug(slug: string): Promise<CategoryApiResponse> {
    try {
      // Prefer explicit slug route if available
      let response: any;
      try {
        response = await api.get<CategoryApiResponse>(`/categories/slug/${encodeURIComponent(slug)}`);
      } catch (firstErr: any) {
        // Fallback: some backends use the same endpoint for id or slug
        response = await api.get<CategoryApiResponse>(`/categories/${encodeURIComponent(slug)}`);
      }
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error fetching category by slug:', error);
      throw new Error('Failed to fetch category details');
    }
  }

  static async createCategory(categoryData: CreateCategoryRequest): Promise<CategoryApiResponse> {
    try {
      const response = await api.post<CategoryApiResponse>('/categories/create', categoryData);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error creating category:', error);
      throw new Error('Failed to create category');
    }
  }

  static async updateCategory(slug: string, updateData: UpdateCategoryRequest): Promise<CategoryApiResponse> {
    try {
      const response = await api.put<CategoryApiResponse>(`/categories/${slug}`, updateData);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error updating category:', error);
      throw new Error('Failed to update category');
    }
  }

  static async deleteCategory(slug: string): Promise<CategoryApiResponse> {
    try {
      const response = await api.delete<CategoryApiResponse>(`/categories/${slug}`);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error deleting category:', error);
      throw new Error('Failed to delete category');
    }
  }

  // Secondary Categories APIs

  static async getSecondaryCategories(primarySlug: string, isActive?: boolean): Promise<CategoryApiResponse> {
    try {
      const url = isActive !== undefined 
        ? `/categories/${primarySlug}/secondary?isActive=${isActive}`
        : `/categories/${primarySlug}/secondary`;
        
      console.log('Making API request to:', url);
      const response = await api.get<any>(url);
      console.log('API response:', response.data);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Handle different possible response structures
      if (response.data.success === false) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      // If the response has a success field and it's true, return as is
      if (response.data.success === true) {
        // Check if the data has secondaryCategories property and extract it
        if (response.data.data && response.data.data.secondaryCategories && Array.isArray(response.data.data.secondaryCategories)) {
          return {
            success: true,
            data: response.data.data.secondaryCategories
          };
        }
        return response.data;
      }
      
      // Check if the response has secondaryCategories property
      if (response.data.secondaryCategories && Array.isArray(response.data.secondaryCategories)) {
        return {
          success: true,
          data: response.data.secondaryCategories
        };
      }
      
      // If the response doesn't have a success field, assume it's the data directly
      // This might be the case for secondary categories endpoint
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error fetching secondary categories:', error);
      throw new Error('Failed to fetch secondary categories');
    }
  }

  static async createSecondaryCategory(
    primarySlug: string, 
    secondaryData: CreateSecondaryCategoryRequest
  ): Promise<CategoryApiResponse> {
    try {
      const response = await api.post<CategoryApiResponse>(
        `/categories/${primarySlug}/secondary`, 
        secondaryData
      );
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error creating secondary category:', error);
      throw new Error('Failed to create secondary category');
    }
  }

  static async updateSecondaryCategory(
    primarySlug: string, 
    secondarySlug: string, 
    updateData: UpdateSecondaryCategoryRequest
  ): Promise<CategoryApiResponse> {
    try {
      const response = await api.put<CategoryApiResponse>(
        `/categories/${primarySlug}/secondary/${secondarySlug}`, 
        updateData
      );
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error updating secondary category:', error);
      throw new Error('Failed to update secondary category');
    }
  }

  static async deleteSecondaryCategory(
    primarySlug: string, 
    secondarySlug: string
  ): Promise<CategoryApiResponse> {
    try {
      const response = await api.delete<CategoryApiResponse>(
        `/categories/${primarySlug}/secondary/${secondarySlug}`
      );
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error deleting secondary category:', error);
      throw new Error('Failed to delete secondary category');
    }
  }
}

export default CategoryService;
